import { useCallback, useEffect, useState } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { Loader2, ShieldAlert, ShieldCheck } from "lucide-react";
import SEOMeta from "@/components/SEOMeta";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import {
  useApproveOAuthAuthorization,
  useDenyOAuthAuthorization,
  useIsAdminUser,
  useOAuthAuthorization,
  useRevokeOAuthApp,
} from "@/hooks/useOAuthConsent";
import { isAllowedOAuthRedirect } from "@/lib/oauthConsent";
import logo from "@/assets/brand/simbolo.svg";

/**
 * /oauth/consent?authorization_id=… — tela para onde o Supabase Auth (OAuth
 * 2.1 Server) manda o admin quando o Claude pede acesso ao MCP do dashboard.
 * Só admin aprova, e o código só segue para redirects do Claude
 * (isAllowedOAuthRedirect); qualquer outro app tem o acesso revogado na hora.
 * Regras: docs/specs/flows/dashboard-api-mcp.md R13.
 */
function hostOf(uri: string): string {
  try {
    return new URL(uri).host;
  } catch {
    return uri;
  }
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid min-h-screen place-items-center bg-background p-4">
      <div className="flex w-full max-w-[440px] flex-col gap-[22px]">
        <div className="flex flex-col items-center gap-3.5">
          <img src={logo} alt="" className="h-[52px] w-[52px] object-contain" />
          <p className="text-[13px] text-muted-foreground">Painel · eliveutonsouza.com</p>
        </div>
        <div className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-[26px] shadow-card">
          {children}
        </div>
      </div>
    </div>
  );
}

function Message({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <ShieldAlert className="h-8 w-8 text-bad" aria-hidden />
      <h1 className="text-[18px] font-bold tracking-[-0.02em]">{title}</h1>
      <p className="text-[13.5px] leading-relaxed text-muted-foreground">{children}</p>
    </div>
  );
}

function Spinner({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
      {label}
    </div>
  );
}

function OAuthConsentView() {
  const [params] = useSearchParams();
  const location = useLocation();
  const authorizationId = params.get("authorization_id");
  const { user, loading: authLoading } = useAuth();
  const adminQuery = useIsAdminUser(user?.id);
  const isAdmin = adminQuery.data === true;
  const details = useOAuthAuthorization(authorizationId, !!user && isAdmin);
  const approve = useApproveOAuthAuthorization();
  const deny = useDenyOAuthAuthorization();
  const revoke = useRevokeOAuthApp();
  const [outcome, setOutcome] = useState<"blocked" | "denied" | "revoke_failed" | null>(null);

  // Registro de cliente é aberto: app que devolve o código para fora do
  // Claude perde o grant na hora. Se a revogação falhar, o admin precisa saber.
  const { mutateAsync: revokeApp } = revoke;
  const block = useCallback(
    async (clientId: string) => {
      try {
        await revokeApp(clientId);
        setOutcome("blocked");
      } catch {
        setOutcome("revoke_failed");
      }
    },
    [revokeApp],
  );

  // Consentimento já dado antes: o Supabase devolve o redirect pronto.
  const alreadyApproved = details.data?.redirectUrl ?? null;
  const approvedClientId = details.data?.clientId ?? null;
  useEffect(() => {
    if (!alreadyApproved || !approvedClientId) return;
    if (isAllowedOAuthRedirect(alreadyApproved)) window.location.assign(alreadyApproved);
    else void block(approvedClientId);
  }, [alreadyApproved, approvedClientId, block]);

  if (!authorizationId) {
    return (
      <Message title="Link de autorização inválido">
        Volte ao Claude e inicie a conexão de novo.
      </Message>
    );
  }
  if (authLoading) return <Spinner label="Carregando…" />;
  if (!user) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/admin/login?next=${encodeURIComponent(next)}`} replace />;
  }
  if (adminQuery.isLoading) return <Spinner label="Verificando permissão…" />;
  if (!isAdmin) {
    return (
      <Message title="Acesso restrito">
        Só administradores do painel podem conectar o Claude. Entre com uma conta de administrador.
      </Message>
    );
  }
  if (outcome === "revoke_failed") {
    return (
      <Message title="App não autorizado">
        Este pedido não veio do Claude e foi barrado, mas não consegui remover a autorização.
        Revogue o app em Configurações › API › Apps conectados.
      </Message>
    );
  }
  if (outcome === "blocked") {
    return (
      <Message title="App não autorizado">
        Este pedido não veio do Claude, então o acesso foi recusado. Só o Claude (claude.ai, Desktop
        e celular) pode se conectar ao painel.
      </Message>
    );
  }
  if (outcome === "denied") {
    return (
      <Message title="Acesso negado">
        Nenhum acesso foi concedido. Você já pode fechar esta aba.
      </Message>
    );
  }
  if (details.isLoading || alreadyApproved) return <Spinner label="Carregando pedido…" />;
  if (details.isError || !details.data) {
    return (
      <Message title="Pedido expirado ou inválido">
        Volte ao Claude e conecte de novo. O pedido de autorização vale por poucos minutos.
      </Message>
    );
  }

  const request = details.data;
  const busy = approve.isPending || deny.isPending || revoke.isPending;

  const onApprove = async () => {
    try {
      const redirectUrl = await approve.mutateAsync(request.authorizationId);
      if (isAllowedOAuthRedirect(redirectUrl)) {
        window.location.assign(redirectUrl);
        return;
      }
      // Um app que se diz "Claude" mas devolve o código para outro domínio
      // perde o acesso antes de recebê-lo.
      await block(request.clientId);
    } catch {
      setOutcome("denied");
    }
  };

  const onDeny = async () => {
    try {
      const redirectUrl = await deny.mutateAsync(request.authorizationId);
      if (redirectUrl && isAllowedOAuthRedirect(redirectUrl)) {
        window.location.assign(redirectUrl);
        return;
      }
    } catch {
      // Negar falhou: nada foi concedido de qualquer forma.
    }
    setOutcome("denied");
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center gap-2 text-center">
        <ShieldCheck className="h-8 w-8 text-primary" aria-hidden />
        <h1 className="text-[19px] font-bold tracking-[-0.02em]">
          Conectar {request.clientName || "app"} ao painel?
        </h1>
        <p className="text-[13px] text-muted-foreground">
          Entrando como <strong className="text-foreground">{user.email}</strong>
        </p>
      </div>

      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-xl border border-border bg-muted/40 p-4 text-[12.5px]">
        <dt className="text-muted-foreground">App</dt>
        <dd className="m-0">
          <span className="font-semibold">{request.clientName || "—"}</span>
          {request.clientUri && (
            <span className="font-mono text-[12px] text-muted-foreground">
              {" "}
              · {hostOf(request.clientUri)}
            </span>
          )}
          <span className="block text-[11.5px] text-muted-foreground">
            Nome e site informados pelo próprio app. O painel só devolve o acesso ao claude.ai.
          </span>
        </dd>
        <dt className="text-muted-foreground">Acesso</dt>
        <dd className="m-0">
          <strong>Administrador</strong> — o mesmo de uma chave de API admin: ler, criar, editar e
          excluir leads, propostas, leads frios, blog e projetos.
        </dd>
      </dl>

      <p className="rounded-xl border border-warn/30 bg-warn-soft p-3 text-[12px] leading-relaxed text-warn">
        Dados de leads frios vêm de scraping de terceiros. Peça ao Claude para confirmar antes de
        excluir algo e revogue o acesso em Configurações › API quando não precisar mais.
      </p>

      <div className="flex flex-col gap-2 sm:flex-row-reverse">
        <Button
          type="button"
          className="h-11 flex-1 rounded-[11px] text-[13.5px] font-bold"
          onClick={onApprove}
          disabled={busy}
        >
          {approve.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
          Autorizar
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-11 flex-1 rounded-[11px] text-[13.5px] font-semibold"
          onClick={onDeny}
          disabled={busy}
        >
          Negar
        </Button>
      </div>
    </div>
  );
}

export default function OAuthConsent() {
  return (
    <>
      <SEOMeta
        title="Autorizar acesso ao painel"
        description="Página privada de autorização de apps."
        noindex
      />
      <Shell>
        <OAuthConsentView />
      </Shell>
    </>
  );
}
